// constants/familyPlanningGoals.js
//
// One place for everything that depends on the user's family planning goal.
// Change GOAL_FEATURES / GOAL_STEPS here — the pages read from this file.

export const FAMILY_PLANNING_GOALS = [
  { value: 'planning_pregnancy', label: 'Planning Pregnancy' },
  { value: 'currently_pregnant', label: 'Currently Pregnant' },
  { value: 'ivf_journey', label: 'IVF Journey' },
  { value: 'egg_donation', label: 'Egg Donation' },
  { value: 'surrogacy', label: 'Surrogacy' },
  { value: 'fertility_consultation', label: 'Fertility Consultation' },
];

export const GOAL_LABELS = Object.fromEntries(
  FAMILY_PLANNING_GOALS.map((g) => [g.value, g.label])
);

export function goalLabel(value) {
  return GOAL_LABELS[value] || '------';
}

// ---------------------------------------------------------------------------
// Which goals see each goal-specific feature.
// Features not listed here are shown to everyone.
// ---------------------------------------------------------------------------
export const GOAL_FEATURES = {
  // "Couple DNA & Birth Risk" tab on the Health page
  couple_dna: ['planning_pregnancy', 'currently_pregnant', 'ivf_journey', 'fertility_consultation'],
};

// true if a user with this goal should see the feature.
// No goal chosen yet -> show everything (nothing is hidden before they pick).
export function goalHasFeature(goal, feature) {
  if (!goal) return true;
  const allowed = GOAL_FEATURES[feature];
  return allowed ? allowed.includes(goal) : true;
}

// ---------------------------------------------------------------------------
// Default checklist per goal — shown on the dashboard until the backend has
// real milestones for the user.
// ---------------------------------------------------------------------------
const GOAL_STEPS = {
  planning_pregnancy: [
    'Complete your health history',
    'Upload both partners’ carrier screening reports',
    'Run the Couple DNA & birth risk analysis',
    'Book a preconception consultation',
    'Set a daily prenatal vitamin reminder',
  ],
  currently_pregnant: [
    'Complete your health history',
    'Upload your latest prenatal lab results',
    'Log your blood pressure weekly',
    'Book your next prenatal check-up',
    'Set medication and vitamin reminders',
  ],
  ivf_journey: [
    'Complete your health history',
    'Upload fertility panel results (AMH, semen analysis)',
    'Run the Couple DNA & birth risk analysis',
    'Book your IVF consultation',
    'Set injection / medication reminders',
  ],
  egg_donation: [
    'Complete your donor health history',
    'Upload your screening and lab results',
    'Book your donor screening appointment',
  ],
  surrogacy: [
    'Complete your health history',
    'Upload the relevant medical reports',
    'Book a surrogacy consultation',
  ],
  fertility_consultation: [
    'Complete your health history',
    'Upload your latest lab results',
    'Book your fertility consultation',
  ],
};

const GENERIC_STEPS = [
  'Choose your family planning goal',
  'Complete your health history',
  'Upload your latest lab results',
  'Book your first appointment',
];

export function defaultStepsForGoal(goal) {
  const labels = GOAL_STEPS[goal] || GENERIC_STEPS;
  return labels.map((label, i) => ({ id: `default-${goal || 'none'}-${i}`, label, done: false }));
}